import { PropsWithChildren, useEffect, useState } from "react";
import { ColorOverlayFilter } from "pixi-filters";
import BoundingBox from "../../Domain/Types/BoundingBox.type";
import { PlaceVariant } from "../../Domain/Types/FloorPlan/PlaceVariant.type";
import { ParkPlaceSprite } from "./Sprite/ParkPlaceSprite";
import { WorkPlaceSprite } from "./Sprite/WorkPlaceSprite";
import { ToolSelection } from "../Views/CreateFloorPlanView/CreateFloorPlanView";
import { prefersPlaceGraphics } from "./functions/detectBrowser.function";

interface Props {
  boundingBox: BoundingBox;
  isSelected?: boolean;
  colorOverlay?: number;
  compatibilityFill?: number;
  isHighlighted?: boolean;
  isFavorite?: boolean;
  isSelectable: boolean;
  variant: PlaceVariant;
  placeTypeId: number | undefined;
  placeScale: number;
  icon?: string;
  tool?: ToolSelection;
  isColleaguesPlace?: boolean;
  /** Desk angle in degrees. Partial pies counter-rotate so the split stays vertical. */
  rotation?: number;
}

export default function PlaceSprite({
  boundingBox,
  isSelected = false,
  colorOverlay,
  compatibilityFill,
  isHighlighted,
  isFavorite = false,
  isSelectable,
  variant,
  placeTypeId = 1,
  placeScale,
  icon,
  tool,
  isColleaguesPlace,
  rotation = 0
}: Readonly<PropsWithChildren<Props>>) {
  //
  // Color
  //
  const selectedColor = 0x0027eb;
  const defaultDeskColor = 0x28b869;
  const occupiedDeskColor = 0xff0000;
  const disabledDeskColor = 0xd2dae1;
  const forbiddenDeskColor = 0xd2dae1; // 0xca61cb;
  const tempDeskColor = 0xdddd56;

  const selectedColorFilter = [new ColorOverlayFilter(selectedColor, 0.6)];
  const [selectedFilter, setSelectedFilter] = useState<ColorOverlayFilter[]>([]);

  const variantColor = (value: PlaceVariant) => {
    switch (value) {
      case PlaceVariant.AVAILABLE:
        return defaultDeskColor;
      case PlaceVariant.BLOCKED:
        return defaultDeskColor;
      case PlaceVariant.OCCUPIED:
        return occupiedDeskColor;
      case PlaceVariant.PARTIAL:
        return occupiedDeskColor;
      case PlaceVariant.WEAK:
        return disabledDeskColor;
      case PlaceVariant.FORBIDDEN:
        return forbiddenDeskColor;
      case PlaceVariant.TEMP:
        return tempDeskColor;
    }
  };

  /**
   * deprecated capacity colorOverlay
   * @used const capacityFilters = useMemo(() => { if (!colorOverlay) return []; return [new ColorOverlayFilter(colorOverlay, 0.6)]; }, [colorOverlay]);
   *
   * Color Overlay Filter to show capacity rates, used in the ReportFloorPlan
   * and using itself is expensive and much more because it covers all the places which has capacity rates
   *
   * deprecated const highlightedColor = 0xeeee00;
   * @used const highlightFilter = isHighlighted ? [new ColorOverlayFilter(highlightedColor, 0.2)] : [];
   *
   * Color Overlay Filter is applied to places which are belonged to the selected zone, used in the CreateFloorPlan and ReportFloorPlan
   *
   * @before const filterGroup = [...capacityFilters, ...highlightFilter, ...selectedFilter];
   * @summary it is replaced by normal color fill to improve performance
   */
  const filterGroup = [...selectedFilter];

  useEffect(() => {
    if (isSelected) {
      setSelectedFilter(selectedColorFilter);
      return;
    }
    setSelectedFilter([]);
  }, [isSelected]);

  /**
   * Firefox WebGL cannot reliably draw Pixi SVG sprites. Read the UA on this render
   * so create / display / report never first-paint the sprite path in Firefox.
   */
  const isGrpPreferred = prefersPlaceGraphics(navigator.userAgent);

  return (
    <CreatePlaceSprite
      placeTypeId={placeTypeId}
      placeScale={placeScale}
      boundingBox={boundingBox}
      filterGroup={filterGroup}
      variantColor={variantColor(variant)}
      variant={variant}
      isSelectable={isSelectable}
      tool={tool}
      icon={icon}
      isColleaguesPlace={isColleaguesPlace}
      isHighlighted={isHighlighted}
      isFavorite={isFavorite}
      colorOverlay={colorOverlay}
      compatibilityFill={compatibilityFill}
      isGrpPreferred={isGrpPreferred}
      rotation={rotation}
    />
  );
}

type CreatePlaceSpriteProps = {
  placeTypeId: number;
  placeScale: number;
  boundingBox: BoundingBox;
  filterGroup: ColorOverlayFilter[];
  variantColor: number;
  variant: PlaceVariant;
  isSelectable: boolean;
  isHighlighted?: boolean;
  isFavorite?: boolean;
  colorOverlay: number | undefined;
  compatibilityFill?: number;
  tool?: ToolSelection;
  icon?: string;
  isColleaguesPlace?: boolean;
  isGrpPreferred: boolean;
  rotation?: number;
};

export function CreatePlaceSprite({
  placeTypeId,
  placeScale,
  boundingBox,
  filterGroup,
  variantColor,
  variant,
  isSelectable,
  isHighlighted,
  isFavorite = false,
  colorOverlay,
  compatibilityFill,
  tool,
  icon,
  isColleaguesPlace,
  isGrpPreferred,
  rotation = 0
}: Readonly<CreatePlaceSpriteProps>) {
  // this sprite is shared for park place and electric charging station
  const parkPlaceSprite = (
    <ParkPlaceSprite
      placeTypeId={placeTypeId}
      boundingBox={boundingBox}
      filterGroup={filterGroup}
      variantColor={variantColor}
      variant={variant}
      placeScale={placeScale}
      isSelectable={isSelectable}
      icon={icon}
      tool={tool}
      isColleaguesPlace={isColleaguesPlace}
      isHighlighted={isHighlighted}
      isFavorite={isFavorite}
      colorOverlay={colorOverlay}
      compatibilityFill={compatibilityFill}
      isGrpPreferred={isGrpPreferred}
    />
  );

  const workPlaceSprite = (
    <WorkPlaceSprite
      boundingBox={boundingBox}
      filterGroup={filterGroup}
      variantColor={variantColor}
      variant={variant}
      placeScale={placeScale}
      isSelectable={isSelectable}
      tool={tool}
      isColleaguesPlace={isColleaguesPlace}
      isHighlighted={isHighlighted}
      isFavorite={isFavorite}
      colorOverlay={colorOverlay}
      compatibilityFill={compatibilityFill}
      isGrpPreferred={isGrpPreferred}
      rotation={rotation}
    />
  );

  switch (placeTypeId) {
    case 1: // normal workplace type
      return workPlaceSprite;
    case 2: // car parking place
      return parkPlaceSprite;
    case 4: // electric charging station
      return parkPlaceSprite;
    default:
      return null;
  }
}
